import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    AutocompleteWithLoading,
    ConfirmationDialogComponent,
    DisabledOverlay,
    EditStyleLimitMessage,
    FileUploadComponent,
    FileUploadImagePreviewComponent,
    FormWizardTemplate,
    MultipleValueTextInput,
    FormWizardTitle,
} from "@iventis/components";
import {
    StyledFieldLabel,
    screenSizeBreakpoints,
    borderRadius,
    formGap,
    media,
    inlineTextIconMargin,
    iconButtonSize,
    InteractiveElement,
    inputMarginBottom,
    muiInputFormsCSS,
    styled,
} from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Box, LinearProgress, useTheme } from "@mui/material";
import React, { FunctionComponent, useCallback, useContext, useMemo, useRef, useState, useTransition } from "react";
import { fileToBase64, getFilteredDataFieldTypesForLayer, useFunctionality, useWindowSize } from "@iventis/utilities";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { StyleValue } from "@iventis/domain-model/model/styleValue";
import { FormWizardEditableTitle } from "@iventis/components/src/form-wizard-editable-title";
import { ExtractRefFromComponent } from "@iventis/types/useful.types";
import { ChipsErrorBoundary, DataFieldConfigContext, DataFieldsChips, DataFieldsChipsProps, DataFieldServicesContext, useDataFieldServices } from "@iventis/datafield-editor";
import { IventisErrorBoundary } from "@iventis/error-boundaries";
import { setLayerStyle } from "@iventis/map-engine/src/utilities/layer.helpers";
import { getLayerStyle } from "@iventis/layer-style-helpers/src/get-layer-style-helpers";
import { styleTypeToLayerProperties, isStyleValid, isLayerStyleValid } from "@iventis/map-engine/src/utilities/style-helpers";
import { UnionOfStyles } from "@iventis/map-engine/src/types/store-schema";
import { isModelLayer } from "@iventis/map-engine/src/utilities/state-helpers";
import { updateLayerDataFieldsOnStyleTypeChange } from "@iventis/datafields";
import { isUsingZoomableStyleValue, isValidZoomableStyleValue } from "@iventis/layer-style-helpers/src/style-value-zoomable-value-helpers";
import { useEditLayerServices } from "./edit-layer-services";
import { EditStyleHeaderComponent, EditStyleComponent } from "./edit-style";
import { EditStyleContext } from "./edit-style-context";
import { LayerTourContext, tourLayerAttributesClassName, tourLayerNameClassName } from "./layer-tour";
import PreviewContainerComponent, { PreviewContainerCallbackRefs } from "./preview-container";
import { AttributeTour, AttributeTourContext } from "./attribute-tour";
import { getStyleDefaultValue, createLayerStyleChangesSideEffects, hasLayerChanged } from "./edit-layer-helpers";
import { EditLayerMapObjectToolTipEditorComponent } from "./edit-layer-map-object-tooltip-editor";
import { useLayerEditor } from "./use-layer-editor";
import { useDataFieldInterceptor } from "./use-data-field-interceptor";

interface ImagePreviewState {
    fileName: string;
    imageUrl: string;
}

export const EditStyleWizard: FunctionComponent<{
    categoryOptions?: { categories: string[]; getExistingCategories: () => Promise<string[]> };
    allowImageUpload?: boolean;
    initialLayer: MapLayer;
    canUserEdit: boolean;
    navigateToProjectSettings?: () => void;
    close: () => void;
    templateImage?: string;
    back: () => void;
    mapName: string;
    showMapObjectDataFieldSelectionForTooltip: boolean;
}> = ({
    categoryOptions,
    allowImageUpload = false,
    initialLayer,
    canUserEdit,
    navigateToProjectSettings,
    close,
    mapName,
    templateImage,
    back,
    showMapObjectDataFieldSelectionForTooltip,
}) => {
    /*
        Globals
    */
    const { screenWidth } = useWindowSize();
    const translate = useIventisTranslate();
    const functionality = useFunctionality();
    const queryClient = useQueryClient();
    const { assetService, styleService: editStyleService, layerService } = useEditLayerServices();
    const { dataFieldsService, dataFieldListItemsService } = useDataFieldServices();

    /*
        UI-only local state
    */
    const [showStyle, setShowStyle] = useState(true);
    const [showAddAttributeTour, setShowAddAttributeTour] = useState(false);
    const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
    const [layerTemplateCategories, setTemplateCategories] = useState<string[]>([]);
    const previewRef = useRef<PreviewContainerCallbackRefs>(null);
    const editLayerTitleRef = useRef<ExtractRefFromComponent<typeof FormWizardEditableTitle>>(null);
    const [thumbnailPreview, setThumbnailPreview] = useState<ImagePreviewState>({ fileName: "template image", imageUrl: templateImage });

    /*
        Queries
    */
    const { data: existingLayerTemplateCategories, isFetching: isLoadingCategories } = useQuery({
        queryKey: ["template-categories", initialLayer.id],
        queryFn: async () => {
            const options = await categoryOptions.getExistingCategories();
            setTemplateCategories(options);
            return options;
        },
        enabled: categoryOptions?.getExistingCategories != null && initialLayer?.id != null,
        initialData: [],
    });

    /*
        Layer editor hook — state, saves, debouncing
    */
    const { savedLayer, latestLayer, setPartialLayer, saveLayer, isSavingLayer, orderedDataFields, existsOnServer, layerId, saveDebounceRef } = useLayerEditor({
        initialLayer,
        layerService,
        dataFieldsService,
        existingLayerTemplateCategories,
        layerTemplateCategories,
        thumbnailPreview,
    });

    /*
        Mutations
    */
    const { isLoading: isUpdatingSyle, mutateAsync: updateStyleType } = useMutation({
        mutationFn: async (newStyleType: StyleType) => {
            const projectDataFields = await editStyleService.getProjectDataFields();
            const updatedDataFields = updateLayerDataFieldsOnStyleTypeChange(newStyleType, latestLayer, projectDataFields);
            let layerStyle = latestLayer[styleTypeToLayerProperties[newStyleType]];
            if (!isStyleValid(layerStyle)) {
                layerStyle = await getLayerStyleForStyleType(newStyleType);
            }
            setPartialLayer({ [styleTypeToLayerProperties[newStyleType]]: layerStyle, styleType: newStyleType, dataFields: updatedDataFields });
        },
    });

    const { isFetching: isLoadingStyle } = useQuery({
        queryKey: ["style", initialLayer.id ?? "new", initialLayer.styleType],
        enabled: !(Object.keys(getLayerStyle(initialLayer)).length > 0),
        queryFn: async () => {
            const { styleType } = latestLayer;
            const layerStyle = await getLayerStyleForStyleType(styleType);
            setPartialLayer({ [styleTypeToLayerProperties[styleType]]: layerStyle, styleType });
            return null;
        },
    });

    /*
        Derived state — canSave
    */
    const canSave = (() => {
        if (!canUserEdit || isUpdatingSyle || isLoadingCategories || isLoadingStyle) {
            return false;
        }

        const layerStyle = getLayerStyle(latestLayer);

        const isValidZoomableValues = Object.values(layerStyle).every((styleValue) => {
            if (isUsingZoomableStyleValue(styleValue)) {
                return isValidZoomableStyleValue(styleValue);
            }
            return true;
        });

        if (!isValidZoomableValues) {
            return false;
        }

        const isDefined = latestLayer.styleType != null && typeof layerStyle === "object" && latestLayer.name !== undefined;
        if (!isDefined) {
            return false;
        }

        if (!existsOnServer) {
            return true;
        }

        return hasLayerChanged(savedLayer, latestLayer, existingLayerTemplateCategories, layerTemplateCategories, templateImage, thumbnailPreview?.imageUrl);
    })();

    /*
        Data field interceptor — wraps CRUD with local layer state updates
    */
    const dataFieldsServiceInterceptor = useDataFieldInterceptor({
        dataFieldsService,
        dataFieldListItemsService,
        setPartialLayer,
        existsOnServer,
        layerId,
        orderedDataFieldCount: orderedDataFields.length,
    });

    // Memoize the config context value to avoid cascading re-renders
    const dataFieldConfig = useMemo(() => ({ resourceId: latestLayer.id, allowSetDefaultValue: true, validateListItem: () => true }), [latestLayer.id]);

    /*
        Functions
    */
    const getLayerStyleForStyleType = async (styleType: StyleType) => {
        let layerStyle: UnionOfStyles;
        if (latestLayer.id !== undefined) {
            layerStyle = await editStyleService.getLayerStyle(latestLayer.id, latestLayer.mapId, styleType);
        }
        if (!isStyleValid(layerStyle)) {
            layerStyle = await getStyleDefaultValue(styleType, editStyleService.getDefaultModel, assetService.getAssetsByType, queryClient);
        }
        return layerStyle;
    };

    const onStylePropertyChanged = useCallback(
        (property: string, value: StyleValue<unknown>) => {
            const styleTypeKey = styleTypeToLayerProperties[latestLayer.styleType];
            setPartialLayer({
                [styleTypeKey]: {
                    ...createLayerStyleChangesSideEffects(latestLayer.styleType, property, value),
                    [property]: value,
                },
            });
        },
        [latestLayer.styleType, setPartialLayer]
    );

    const onImageSelected = async (setState: (value: React.SetStateAction<ImagePreviewState>) => void, file?: File) => {
        if (!file) {
            setState(undefined);
            return;
        }
        const { withHeader } = await fileToBase64(file);
        setState({ fileName: file.name, imageUrl: withHeader });
    };

    const handleStyleTypeChange = async (styleType: StyleType) => {
        const style = getLayerStyle({ ...latestLayer, styleType });
        if (!isStyleValid(style)) {
            const defaultStyle = await getStyleDefaultValue(styleType, editStyleService.getDefaultModel, assetService.getAssetsByType, queryClient);
            const layer = setLayerStyle(latestLayer, defaultStyle);
            setPartialLayer(layer);
        } else {
            setPartialLayer({ styleType });
        }
    };

    /** Cancel pending debounce and save when opening a chip editor */
    const handleChipOpen = useCallback(() => {
        saveDebounceRef.current.cancel();
        saveLayer(undefined);
    }, [saveLayer, saveDebounceRef]);

    const isLoading = isLoadingStyle || isUpdatingSyle || !isLayerStyleValid(latestLayer);

    return (
        <AttributeTour running={showAddAttributeTour} onFinish={() => setShowAddAttributeTour(false)}>
            <FormWizardTemplate
                currentStage={0}
                stages={[
                    {
                        isValid: canSave && !isSavingLayer,
                        primaryButtonCallback: async () => {
                            await saveLayer(latestLayer);
                        },
                        submitButtonDataCy: "save-layer-button",
                        primaryButtonText: translate(Content.common.buttons.save),
                        submittingText: translate(Content.common.saving),
                        submittedText: translate(Content.common.saved),
                        showLoadingSpinner: isSavingLayer,
                        isSubmitted: !isSavingLayer && !canSave,
                        secondaryButtons: [
                            {
                                buttonDisabled: isSavingLayer,
                                onButtonPressed: existsOnServer ? close : back,
                                buttonText: translate(Content.common.buttons[existsOnServer ? "close" : "back"]),
                                dataCy: "close-layer-editor-button",
                            },
                        ],
                    },
                ]}
                title={
                    <FormWizardTitle
                        header={mapName}
                        title={
                            <FormWizardEditableTitle
                                ref={editLayerTitleRef}
                                entityName={latestLayer.name}
                                fallbackTitle={latestLayer.name}
                                updateNameOnEntity={(name) => name !== savedLayer.name && setPartialLayer({ name })}
                                disableNameEdits={!canUserEdit}
                                className={tourLayerNameClassName}
                                icon={<FontAwesomeIcon style={{ marginRight: inlineTextIconMargin }} icon={["far", "layer-group"]} />}
                            />
                        }
                    />
                }
                dialogActions={{ close: true, toggleFullScreen: true }}
                modalTestId="edit-layer-wizard"
            >
                <ConfirmationDialogComponent
                    show={showDiscardConfirmation}
                    handleConfirm={navigateToProjectSettings}
                    handleCancel={() => setShowDiscardConfirmation(false)}
                    title={translate(Content.map5.edit_layer.discard_title)}
                    message={translate(Content.map5.edit_layer.discard_message)}
                    confirmText={translate(Content.common.yes)}
                    cancelText={translate(Content.common.no)}
                />
                <StyledContent data-testid="edit-style-wizard">
                    <StyleTypeEditor layer={latestLayer} layerExistsOnServer={latestLayer.id?.length > 0} setStyleType={handleStyleTypeChange} />
                    {categoryOptions != null && (
                        <div>
                            <MultipleValueTextInput
                                values={categoryOptions.categories}
                                selectedValues={layerTemplateCategories}
                                title={translate("Categories")}
                                placeholder={translate("Search for categories")}
                                onChange={(value) => setTemplateCategories(value)}
                                disabled={isLoadingCategories}
                                sectionalMargin={false}
                            />
                        </div>
                    )}
                    {allowImageUpload && (
                        <ImageUploadersContainer>
                            <div>
                                <StyledFieldLabel>{translate("Upload preview image")}</StyledFieldLabel>
                                <FileUploadComponent
                                    PreviewComponent={FileUploadImagePreviewComponent}
                                    previewImageClassName="layer-template-image-preview"
                                    removeRequested={() => onImageSelected(setThumbnailPreview)}
                                    loading={false}
                                    fileName={thumbnailPreview?.fileName}
                                    fileThumbnailUrl={thumbnailPreview?.imageUrl}
                                    uploadFile={(value) => onImageSelected(setThumbnailPreview, value)}
                                    uploadButtonText={translate("Select file")}
                                    removeImageText={translate("Remove image")}
                                    persistFileSelectInput
                                />
                            </div>
                        </ImageUploadersContainer>
                    )}
                    <div className={tourLayerAttributesClassName}>
                        <div className="icon-label">
                            <FontAwesomeIcon icon={["fas", "tag"]} size="xs" />
                            <StyledFieldLabel>{translate(Content.map.data_fields.title)}</StyledFieldLabel>
                        </div>
                        <ChipsErrorBoundary toast={null}>
                            <DataFieldConfigContext.Provider value={dataFieldConfig}>
                                <DataFieldServicesContext.Provider value={dataFieldsServiceInterceptor}>
                                    <DataFieldChipWrapper
                                        dataFields={orderedDataFields}
                                        getProjectDataFields={editStyleService.getProjectDataFields}
                                        allowDeletion
                                        navigateToProjectSettings={() => (canSave ? setShowDiscardConfirmation(true) : navigateToProjectSettings())}
                                        allowEdit={canUserEdit}
                                        allowCreate={canUserEdit}
                                        onOpen={handleChipOpen}
                                        onDragEnd={(dataFields) => {
                                            if (!existsOnServer) saveLayer({ ...latestLayer, dataFields });
                                        }}
                                        filteredDataFieldTypes={getFilteredDataFieldTypesForLayer(latestLayer.styleType)}
                                        layerLoaded={!!savedLayer?.id}
                                        addButtonTestId="style-editor-add-attribute-button"
                                        disableAddButton={isLoading}
                                    />
                                </DataFieldServicesContext.Provider>
                            </DataFieldConfigContext.Provider>
                        </ChipsErrorBoundary>
                    </div>
                    {showMapObjectDataFieldSelectionForTooltip && (
                        <EditLayerMapObjectToolTipEditorComponent
                            layer={latestLayer}
                            setDataFields={(tooltipDataFieldIds) => setPartialLayer({ tooltipDataFieldIds })}
                            disabled={!canUserEdit}
                        />
                    )}
                    {!functionality.customLayer && <EditStyleLimitMessage />}
                    <div>
                        <div className="icon-label">
                            <FontAwesomeIcon icon="palette" size="xs" />
                            <StyledFieldLabel>{translate(Content.map.add_layer.styles)}</StyledFieldLabel>
                        </div>
                        <StyledOptionsContainer>
                            <IventisErrorBoundary>
                                {isLoading && (
                                    <>
                                        <LinearProgress style={{ width: "100%", position: "absolute", top: 0, left: 0, borderRadius: "20px" }} />
                                        <DisabledOverlay />
                                    </>
                                )}
                                {!(isLoadingStyle || isUpdatingSyle || !isLayerStyleValid(latestLayer)) && (
                                    <>
                                        {screenWidth >= screenSizeBreakpoints.extraExtraSmall && (
                                            <PreviewContainerComponent
                                                ref={previewRef}
                                                layerStyle={getLayerStyle(latestLayer)}
                                                className={showStyle ? "style-preview" : "style-preview-small"}
                                                datafields={latestLayer.dataFields}
                                                assetGetter={editStyleService.assetGetter}
                                                modelGetter={editStyleService.modelGetter}
                                                imageCompressUrlGetter={editStyleService.imageCompressor}
                                                isAssetSdf={editStyleService.isAssetSdf}
                                                getAttributeListItems={dataFieldListItemsService.getDataFieldListItems}
                                                bustCacheIds={editStyleService.bustCacheIds}
                                                assetRefreshObserver={editStyleService.refreshAssetEvent}
                                                cypressWindowName="IVENTIS_LAYER_STYLE_PREVIEW_ENGINE_TESTING_FUNCTIONS"
                                            />
                                        )}
                                        {/* TODO: Replace name="style" with layer style name once implemented */}
                                        <EditStyleHeaderComponent
                                            previewRef={previewRef}
                                            style={getLayerStyle(latestLayer)}
                                            name="style"
                                            show={showStyle}
                                            setShow={setShowStyle}
                                            datafields={latestLayer.dataFields}
                                            editStyleService={editStyleService}
                                            imageCompressUrlGetter={editStyleService.imageCompressor}
                                        >
                                            <EditStyleContext.Provider
                                                value={{
                                                    setShowAddAttributeTour: (value) => setShowAddAttributeTour(value),
                                                    canUserEdit: canUserEdit && functionality.customLayer,
                                                    assetService,
                                                    getDefaultModel: editStyleService.getDefaultModel,
                                                    getModelFromThumbnailId: editStyleService.getModelFromThumbnailId,
                                                    modelGetter: editStyleService.modelGetter,
                                                    getProjectDataFields: editStyleService.getProjectDataFields,
                                                    onAssetRefresh: editStyleService.refreshAssetEvent,
                                                }}
                                            >
                                                <EditStyleComponent layer={latestLayer} onChange={onStylePropertyChanged} onStyleTypeChanged={updateStyleType} />
                                            </EditStyleContext.Provider>
                                        </EditStyleHeaderComponent>
                                    </>
                                )}
                            </IventisErrorBoundary>
                        </StyledOptionsContainer>
                    </div>
                </StyledContent>
            </FormWizardTemplate>
        </AttributeTour>
    );
};

export const StyleTypeEditor: FunctionComponent<{ layer: MapLayer; layerExistsOnServer: boolean; setStyleType: (styleType: StyleType) => void }> = ({
    layer,
    layerExistsOnServer,
    setStyleType,
}) => {
    const translate = useIventisTranslate();
    const functionality = useFunctionality();
    const theme = useTheme<Theme>();
    const [, startTransition] = useTransition();

    const [displayStyleType, setDisplayStyleType] = useState(layer.styleType);
    const updateStyleType = (styleType: StyleType) => {
        setDisplayStyleType(styleType);
        startTransition(() => {
            setStyleType(styleType);
        });
    };
    return (
        <AutocompleteWithLoading
            value={displayStyleType || layer.styleType}
            options={Object.values(StyleType)}
            label={translate(Content.map.add_layer.type_of_layer)}
            onChange={updateStyleType}
            loadingOptions={false}
            getOptionLabel={(opt) => translate(Content.map2.styles2[opt])}
            disabled={layerExistsOnServer}
            dataTestId="new-layer-style-type"
            renderOption={(props, option) => (
                <StyledLayerTypeItem key={option}>
                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <Box {...props} key={option} width="100%">
                        {translate(Content.map2.styles2[option])}
                    </Box>
                    {!functionality.layer3DTypes && isModelLayer({ styleType: option }) && (
                        <>
                            <DisabledOverlay />
                            <InteractiveElement className="upgrade-button" onClick={() => functionality.open?.("layer3DTypes")}>
                                <FontAwesomeIcon icon={["far", "circle-up"]} color={theme.primaryColors.focus} className="icon" />
                            </InteractiveElement>
                        </>
                    )}
                </StyledLayerTypeItem>
            )}
        />
    );
};

const DataFieldChipWrapper: FunctionComponent<DataFieldsChipsProps> = (props) => {
    const { ref: layerTourRef } = useContext(LayerTourContext);
    const { ref: attributeTourRef } = useContext(AttributeTourContext);
    return (
        <DataFieldsChips
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            onOpen={() => {
                if (layerTourRef?.current?.isRunning()) {
                    layerTourRef.current.minimiseTour();
                }
                if (attributeTourRef?.current?.isRunning()) {
                    attributeTourRef.current.minimiseTour();
                }
                props?.onOpen?.();
            }}
            onClose={() => {
                if (layerTourRef?.current?.isRunning() && !attributeTourRef?.current?.isRunning()) {
                    layerTourRef.current.maximiseTour();
                    layerTourRef.current.nextStep();
                }
                if (attributeTourRef?.current?.isRunning()) {
                    attributeTourRef.current.maximiseTour();
                    attributeTourRef.current.nextStep();
                }
                props?.onClose?.();
            }}
        />
    );
};

const StyledContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 40px;

    ${muiInputFormsCSS}

    .icon-label {
        margin-bottom: 10px;
    }

    .layer-template-image-preview {
        margin-top: ${inputMarginBottom};
        .image-preview {
            /* Matches the browser-rendered width of the layer template preview */
            height: 84px;
            width: 196px;
            object-fit: cover;
            object-position: top;
        }
    }

    .icon-label svg {
        padding-right: 5px;
        display: inline;
    }
    .icon-label label {
        display: inline;
    }
`;

const StyledLayerTypeItem = styled.div`
    position: relative;
    display: flex;
    .upgrade-button {
        position: absolute;
        right: 0;
        top: 0;
        width: ${iconButtonSize};
        height: ${iconButtonSize};
        z-index: 1000;
    }
`;

export const StyledTitleContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    button {
        margin-right: 10px;
    }
    .xmark {
        width: 18px;
        height: 18px;
        margin-right: 0;
    }
`;

const StyledOptionsContainer = styled.div`
    display: flex;
    border: ${({ theme }: { theme: Theme }) => `1px solid ${theme.shades.three}`};
    border-radius: ${borderRadius.standard};
    padding: ${formGap};
    justify-content: stretch;
    position: relative;

    .style-preview {
        width: 72px;
        height: 72px;
        ${media.small} {
            width: 80px;
            height: 80px;
        }
    }

    .style-preview-small {
        width: 72px;
        height: 72px;
    }
`;

const ImageUploadersContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 50%);
`;
